/**
 * Serviço para gerenciar tokens e cookies de autenticação
 */

const TOKEN_COOKIE_NAME = "ai_visor_token";
const USER_DATA_COOKIE_NAME = "ai_visor_user";

// Configurações de cookie seguras
const COOKIE_OPTIONS = {
  secure: true, // Apenas HTTPS
  httpOnly: false, // Permitir acesso via JavaScript (necessário para SPA)
  sameSite: "strict" as const, // Proteção CSRF - bloqueia outros sites
  path: "/", // Apenas no domínio atual
  maxAge: 24 * 60 * 60, // 24 horas em segundos
  // domain: window.location.hostname, // Limita ao domínio atual
};

export interface Permission {
  name: string;
  display_name: string;
  description: string;
}

export interface Profile {
  id: string;
  name: string;
}

export interface UserData {
  id: string;
  name: string;
  email: string;
  email_verified?: boolean;
  profile?: Profile;
  permissions?: Permission[];
  status?: string;
  access_type?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Salva o token e dados do usuário em cookies seguros
 */
export function saveAuthData(token: string, userData: UserData): void {
  try {
    const domain = window.location.hostname;
    const isLocalhost = domain === "localhost" || domain === "127.0.0.1";
    const isHttps = window.location.protocol === "https:";

    // Configurações específicas para desenvolvimento vs produção
    // Em desenvolvimento, usa sameSite=lax para garantir compatibilidade
    // Em produção, usa sameSite=strict para máxima segurança
    // Só usa 'secure' se estiver em HTTPS (cookies secure não funcionam em HTTP)
    const cookieConfig = isLocalhost
      ? `path=/; max-age=${COOKIE_OPTIONS.maxAge}; samesite=lax`
      : `${isHttps ? 'secure;' : ''} samesite=strict; path=/; max-age=${COOKIE_OPTIONS.maxAge}; domain=${domain}`;

    // Mapear dados do usuário para garantir compatibilidade
    const mappedUserData: UserData = {
      id: userData.id,
      name: userData.name,
      email: userData.email,
      email_verified: userData.email_verified,
      profile: userData.profile,
      permissions: userData.permissions,
      status: userData.status,
      access_type: userData.access_type,
      created_at: userData.created_at,
      updated_at: userData.updated_at,
    };

    // Salvar token (codificar para evitar problemas com caracteres especiais)
    const encodedToken = encodeURIComponent(token);
    document.cookie = `${TOKEN_COOKIE_NAME}=${encodedToken}; ${cookieConfig}`;

    // Salvar dados do usuário
    const userDataJson = JSON.stringify(mappedUserData);
    const encodedUserData = encodeURIComponent(userDataJson);
    const cookieValue = `${USER_DATA_COOKIE_NAME}=${encodedUserData}; ${cookieConfig}`;
    const cookieSize = cookieValue.length;

    // Sempre salvar em localStorage como backup (não tem limite de 4KB)
    try {
      localStorage.setItem(USER_DATA_COOKIE_NAME, userDataJson);
    } catch (error) {
      // Ignorar erro se localStorage não estiver disponível
    }

    // Tentar salvar em cookie se não exceder o limite
    if (cookieSize <= 4096) {
      document.cookie = cookieValue;
    }

  } catch (error) {
    // Ignorar erros ao salvar cookies
  }
}

/**
 * Recupera o token dos cookies
 */
export function getToken(): string | null {
  try {
    const cookies = document.cookie.split(";");
    const tokenCookie = cookies.find((cookie) =>
      cookie.trim().startsWith(`${TOKEN_COOKIE_NAME}=`)
    );

    if (tokenCookie) {
      const tokenValue = tokenCookie.split("=").slice(1).join("=");
      return decodeURIComponent(tokenValue || "");
    }

    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Recupera os dados do usuário dos cookies ou localStorage
 */
export function getUserData(): UserData | null {
  try {
    // Primeiro tentar recuperar do cookie
    if (document.cookie) {
      const cookies = document.cookie.split(";");
      const userCookie = cookies.find((cookie) =>
        cookie.trim().startsWith(`${USER_DATA_COOKIE_NAME}=`)
      );

      if (userCookie) {
        const userDataValue = userCookie.split("=").slice(1).join("=");
        if (userDataValue && userDataValue.trim() !== "") {
          try {
            const userDataString = decodeURIComponent(userDataValue);
            const userData = JSON.parse(userDataString);

            // Validar que os dados essenciais estão presentes
            if (userData && userData.id && userData.email) {
              return userData as UserData;
            }
          } catch (error) {
            // Ignorar erro ao parsear cookie
          }
        }
      }
    }

    // Se não encontrou no cookie, tentar localStorage
    try {
      const userDataString = localStorage.getItem(USER_DATA_COOKIE_NAME);
      if (userDataString) {
        const userData = JSON.parse(userDataString);

        // Validar que os dados essenciais estão presentes
        if (userData && userData.id && userData.email) {
          // Tentar salvar no cookie novamente (pode ter sido perdido)
          try {
            const encodedUserData = encodeURIComponent(userDataString);
            const domain = window.location.hostname;
            const isLocalhost = domain === "localhost" || domain === "127.0.0.1";
            const isHttps = window.location.protocol === "https:";
            const cookieConfig = isLocalhost
              ? `path=/; max-age=${COOKIE_OPTIONS.maxAge}; samesite=lax`
              : `${isHttps ? 'secure;' : ''} samesite=strict; path=/; max-age=${COOKIE_OPTIONS.maxAge}; domain=${domain}`;

            if ((`${USER_DATA_COOKIE_NAME}=${encodedUserData}; ${cookieConfig}`).length <= 4096) {
              document.cookie = `${USER_DATA_COOKIE_NAME}=${encodedUserData}; ${cookieConfig}`;
            }
          } catch (error) {
            // Ignorar erro ao tentar salvar no cookie
          }

          return userData as UserData;
        }
      }
    } catch (error) {
      // Ignorar erro ao recuperar do localStorage
    }

    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Verifica se existe token válido
 */
export function hasValidToken(): boolean {
  const token = getToken();
  return token !== null && token.length > 0;
}

/**
 * Limpa todos os dados de autenticação
 */
export function clearAuthData(): void {
  try {
    const domain = window.location.hostname;
    const isLocalhost = domain === "localhost" || domain === "127.0.0.1";
    const isHttps = window.location.protocol === "https:";

    // Configuração de cookie para limpeza (deve corresponder à configuração de salvamento)
    const cookieConfig = isLocalhost
      ? `path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
      : `${isHttps ? 'secure;' : ''} samesite=strict; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${domain}`;

    // Limpar token - tentar múltiplas formas para garantir limpeza
    document.cookie = `${TOKEN_COOKIE_NAME}=; ${cookieConfig}`;
    document.cookie = `${TOKEN_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    if (!isLocalhost) {
      // Tentar limpar sem domain também
      document.cookie = `${TOKEN_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=.${domain}`;
    }

    // Limpar dados do usuário do cookie - tentar múltiplas formas
    document.cookie = `${USER_DATA_COOKIE_NAME}=; ${cookieConfig}`;
    document.cookie = `${USER_DATA_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    if (!isLocalhost) {
      // Tentar limpar sem domain também
      document.cookie = `${USER_DATA_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=.${domain}`;
    }

    // Limpar dados do usuário do localStorage
    try {
      localStorage.removeItem(USER_DATA_COOKIE_NAME);
    } catch (error) {
      // Ignorar erro se localStorage não estiver disponível
    }

    // Limpar localStorage (caso tenha dados adicionais)
    try {
      localStorage.clear();
    } catch (error) {
      // Ignorar erro se localStorage não estiver disponível
    }

    // Limpar sessionStorage
    try {
      sessionStorage.clear();
    } catch (error) {
      // Ignorar erro se sessionStorage não estiver disponível
    }

    // Verificar se os cookies foram realmente limpos
    const remainingToken = getToken();
    const remainingUserData = getUserData();
    if (remainingToken || remainingUserData) {
      // Se ainda há dados, tentar limpar novamente sem domain
      document.cookie = `${TOKEN_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      document.cookie = `${USER_DATA_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  } catch (error) {
    // Em caso de erro, tentar limpeza básica
    try {
      document.cookie = `${TOKEN_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      document.cookie = `${USER_DATA_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      // Ignorar erros finais
    }
  }
}

/**
 * Verifica se o usuário está autenticado
 */
export function isAuthenticated(): boolean {
  return hasValidToken() && getUserData() !== null;
}
